import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { AccountProvider, useAccount } from "./contexts/AccountContext";
import { CartProvider } from "./contexts/CartContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ShowroomTransitionContext } from "./contexts/ShowroomTransitionContext";
import "./App.css";
import "./styles/Responsiveness.css";
import "./styles/DarkMode.css";
import { useRef, useState, useCallback, useEffect } from "react";
import { setScrollRestore } from './utils/scrollRestore';
import { useApiCall } from "./hooks/useApiCall";
import Experience from "./views/Experience";
import Products from "./views/Products.js";
import Header from "./views/header/Header.js";
import Home from "./views/Home.js";
import Footer from "./views/footer/Footer.js";
import Product from "./views/products/Product.js";
import Cart from "./views/Cart.js";
import Wishlist from "./views/Wishlist.js";

import Signin from "./views/Signin.js";
import Signup from "./views/Signup.js";
import ForgotPassword from "./views/ForgotPassword.js";
import ResetPassword from "./views/ResetPassword.js";

import ScrollToTop from "./components/ScrollToTop.js";
import Aboutus from "./views/Aboutus.js";
import Contact from "./views/Contact.js";
import FAQ from "./views/FAQ.js";
import Account from "./views/Account.js";
import Order from "./views/Order.js";
import Checkout from "./views/Checkout.js";
import Page from "./views/Page.js";
import Projects from "./views/Blogs.js";
import ProjectPost from "./views/BlogPost.js";


function FooterWrapper() {
  const location = useLocation();
  if (location.pathname.startsWith('/experience')) return null;
  return <Footer />;
}

function AppRouter() {
  const { account, updateAccount, clearAccount } = useAccount();
  const apiCall = useApiCall();
  const refreshLoader = useRef();
  const location = useLocation();
  const isExperience = location.pathname.startsWith('/experience');
  const isFullScreen = isExperience;

  // Transition overlay state
  const [transitionPhase, setTransitionPhase] = useState(null); // 'enter' | 'exit' | null
  const [experienceKey, setExperienceKey] = useState(0);
  const prevIsExperienceRef = useRef(isExperience);
  const navigate = useNavigate();

  const savedScrollRef = useRef(null);
  const savedPathRef = useRef('/');

  // Keep savedPathRef updated whenever we're NOT in Experience
  useEffect(() => {
    if (!isExperience) {
      savedPathRef.current = location.pathname + location.search;
    }
  }, [location, isExperience]);

  // Called by Experience instead of navigate() so the overlay is up before the route changes
  const exitExperience = useCallback((dest) => {
    const target = dest ?? savedPathRef.current;
    setTransitionPhase('exit');
    // Wait until overlay is fully opaque (stFadeExit reaches opacity:1 at 30% of 700ms = 210ms)
    setTimeout(() => {
      if (savedScrollRef.current !== null) {
        setScrollRestore(savedScrollRef.current);
        savedScrollRef.current = null;
      }
      navigate(target);
    }, 220);
  }, [navigate]);

  useEffect(() => {
    const prev = prevIsExperienceRef.current;
    prevIsExperienceRef.current = isExperience;
    if (prev === isExperience) return;

    if (isExperience) {
      // Save scroll position so we can restore it when leaving
      savedScrollRef.current = window.scrollY;
      // Entering showroom — remount fresh + play transition
      setExperienceKey(k => k + 1);
      setTransitionPhase('enter');
      const t = setTimeout(() => setTransitionPhase(null), 1100);
      return () => clearTimeout(t);
    } else {
      // Clear any leftover exit overlay
      const t = setTimeout(() => setTransitionPhase(null), 700);
      return () => clearTimeout(t);
    }
  }, [isExperience]);
  const refreshAuth = useCallback(() => {
    if(refreshLoader.current){
      refreshLoader.current.abort();
      refreshLoader.current = null;
    }

    refreshLoader.current = new AbortController();

    apiCall('auth/refresh', 'POST', {}, account, refreshLoader.current).then((response) => {

      if(response.success){

        updateAccount(response.data?.account);
      }
    });
  }, [account, updateAccount]);

  useEffect(() => {
    if(account){
      refreshAuth();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <ShowroomTransitionContext.Provider value={{ exitExperience }}>
    <div className={isFullScreen ? '' : 'root-width'}>
      {/* Showroom transition overlay */}
      {transitionPhase && (
        <div className={`ShowroomTransitionOverlay ShowroomTransition--${transitionPhase}`}>
          {transitionPhase === 'enter' && (
            <div className="ShowroomTransitionLabel">ENTERING SHOWROOM</div>
          )}
        </div>
      )}

      {/* Header is always mounted to preserve counts/categories; hidden on fullscreen pages */}
      <Header style={{ display: isFullScreen ? 'none' : 'block' }} />

      {/* Experience is always mounted to preserve video/state; hidden when not active */}
      <div style={{ display: isExperience ? 'block' : 'none' }}>
        <Experience key={experienceKey} />
      </div>

      <div style={{ display: isExperience ? 'none' : 'block' }}>
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/category/:id" element={<Products />} />
        <Route path="/products/category/:id/:slug" element={<Products />} />
        <Route path="/products/product/:id" element={<Product />} />
        <Route path="/products/product/:id/:slug" element={<Product />} />

        <Route path="/sign-in" element={<Signin />} />
        <Route path="/sign-up" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/wishlist" element={<Wishlist />} />

        <Route path="/contact" element={<Contact />} />
        <Route path="/about-us" element={<Aboutus />} />
        <Route path="/faq" element={<FAQ />} />

        <Route path="/page/:slug" element={<Page />} />

        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:slug" element={<ProjectPost />} />
        <Route path="/blogs" element={<Projects />} />
        <Route path="/blogs/:slug" element={<ProjectPost />} />

        <Route path="/account" element={<Account />} />
        <Route path="/account/:tab" element={<Account />} />
        <Route path="/account/order/:orderId" element={<Order />} />

        {/* Experience is rendered outside Routes (always mounted); this silences the no-match warning */}
        <Route path="/experience" element={null} />
        <Route path="/experience/*" element={null} />
      </Routes>
      </div>

    </div>
    </ShowroomTransitionContext.Provider>
  );
}

export default function App() {


  return (
    <ThemeProvider>
    <ToastProvider>
      <AccountProvider>
        <CartProvider>
          <BrowserRouter basename={process.env.PUBLIC_URL}>
            <ScrollToTop />
            <AppRouter />
            <FooterWrapper />
          </BrowserRouter>
        </CartProvider>
      </AccountProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}
